import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import {
  ArrowUpRight, Check, ChevronRight, ChevronLeft, Plus, Minus,
  MessageCircle, Scale, Cpu, Landmark, PenTool,
} from "lucide-react";
import { useSeo } from "@/lib/seo";
import {
  isValidMonth, getMonthData, getMonthYear, getSampleRows,
  prevNext, MONTH_SLUGS, waLink,
} from "@/lib/mcaMonths";

// Unique per-month angles — rotate so pages aren't duplicated
const ANGLES = [
  "The filing rhythm this month skewed toward services incorporations in metro states — founders spinning up consulting and IT ventures. Teams working this cohort report the fastest replies when they reference the incorporation date in the opening message.",
  "This cohort leaned manufacturing and trading in industrial corridors — Gujarat, Maharashtra and Tamil Nadu carried the volume. Paid-up capital bands above ₹10 lakh made for the strongest conversion in outbound sequences.",
  "Seasonal incorporation patterns show a pickup in LLP formations among professional services — audit season driving new partnerships. CA firms working this month saw strong attach rates on compliance packages.",
  "Metro registrations dominated this month, with Delhi NCR and Mumbai leading the count. Banking outreach to this cohort converted best in the first three weeks after incorporation.",
  "This month's filings included a notable OPC share — solo founders choosing one-person companies for liability protection. SaaS and tools vendors see the shortest sales cycle with this segment.",
  "Tier-2 city incorporations held steady this month, giving teams that look beyond the metros a less crowded field. WhatsApp-first outreach outperformed email for this cohort.",
];

const USE_CASES = [
  { icon: Scale, t: "CA & CS Firms" },
  { icon: Cpu, t: "Software & SaaS" },
  { icon: Landmark, t: "Banks & NBFCs" },
  { icon: PenTool, t: "Trademark & IP Agents" },
];

export default function McaMonth() {
  const { slug } = useParams();
  const [openFaq, setOpenFaq] = useState(0);

  if (!isValidMonth(slug)) {
    return (
      <div data-testid="mca-month-404" className="min-h-[70vh] flex flex-col items-center justify-center bg-white px-6 py-32 text-center">
        <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ 404</div>
        <h1 className="mt-4 font-display text-5xl md:text-7xl font-light tracking-tight text-slate-900">Month not found</h1>
        <p className="mt-6 max-w-md text-slate-600 leading-relaxed">
          We don't have MCA data for this month. Browse all available months instead.
        </p>
        <Link to="/mca-data" className="mt-8 inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 transition-colors">
          Back to all months <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
        </Link>
      </div>
    );
  }

  return <MonthContent slug={slug} openFaq={openFaq} setOpenFaq={setOpenFaq} />;
}

function MonthContent({ slug, openFaq, setOpenFaq }) {
  const data = getMonthData(slug);
  const { monthName, year } = getMonthYear(slug);
  const { prev, next } = prevNext(slug);
  const inProgress = data?.status === "in-progress";
  const monthIdx = MONTH_SLUGS.indexOf(slug);
  const angle = ANGLES[monthIdx % ANGLES.length];
  const wa = waLink(`Hi Datorra, I want MCA data for ${monthName} ${year}`);
  const rows = getSampleRows(slug);

  const FAQS = [
    { q: `Can I buy only ${monthName} ${year} data?`, a: `Yes. The ${monthName} ${year} file is available as a standalone purchase — trial pack ₹3,000 one-time or as part of the monthly plan. Contact us on WhatsApp with your state and company-type filters.` },
    { q: `In what format is the ${monthName} ${year} list delivered?`, a: `Excel (.xlsx) and CSV — delivered within 24 hours of order confirmation. Both formats import cleanly into any CRM or dialler.` },
    { q: "Does the list include LLPs?", a: "Yes. The file covers Pvt Ltd, LLP, OPC, Public Ltd, Section 8 and other MCA-registered entities. You can order an LLP-only cut at no extra charge." },
    { q: "How soon after purchase will I get the file?", a: "Standard delivery is within 24 hours of payment confirmation. Current-month data is delivered daily as new filings are published." },
  ];

  useSeo({
    title: `Newly Registered Companies ${monthName} ${year} | MCA Data Download Excel | Datorra`,
    description: `Buy MCA data for ${monthName} ${year}. Newly registered company list with CIN, director, mobile and email. Excel/CSV download, state-wise filters. Datorra India.`,
    path: `/mca-data/${slug}`,
    jsonLd: [
      {
        id: "mcamonth-breadcrumb",
        data: {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://datorra.in/" },
            { "@type": "ListItem", position: 2, name: "MCA Data", item: "https://datorra.in/mca-data" },
            { "@type": "ListItem", position: 3, name: `${monthName} ${year}`, item: `https://datorra.in/mca-data/${slug}` },
          ],
        },
      },
      {
        id: "mcamonth-faq",
        data: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        },
      },
    ],
  });

  const hasSplit = data?.company_type_split && Object.keys(data.company_type_split).length > 0;
  const hasTopStates = Array.isArray(data?.top_states) && data.top_states.length > 0;
  const hasTotal = typeof data?.total_new_companies === "number";

  return (
    <div data-testid={`mca-month-page-${slug}`} className="bg-white">
      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-20 border-b border-slate-100 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-blue-50/60 blur-3xl rounded-full pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <nav aria-label="Breadcrumb" className="text-xs md:text-sm text-slate-500 flex flex-wrap items-center gap-1.5">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300" strokeWidth={1.75} />
            <Link to="/mca-data" className="hover:text-blue-600">MCA Data</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300" strokeWidth={1.75} />
            <span className="text-slate-900 font-medium">{monthName} {year}</span>
          </nav>

          <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-5xl">
            Newly Registered Companies in <span className="font-serif-editorial italic">{monthName} {year}</span>: MCA Data Download
          </h1>

          <p className="mt-6 max-w-3xl text-lg md:text-xl text-slate-600 leading-relaxed">
            Buy the {monthName} {year} newly registered companies list — CIN, director, mobile and email included.
            Delivered in Excel/CSV with state-wise filters.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="month-hero-wa" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 active:scale-95 transition-all">
              Buy {monthName} {year} Data on WhatsApp <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
            </a>
            <Link to="/mca-data" className="group inline-flex items-center gap-2 rounded-full bg-white text-slate-900 border border-slate-200 pl-6 pr-5 py-4 text-sm font-medium hover:border-slate-900 active:scale-95 transition-all">
              <ChevronLeft className="w-4 h-4" strokeWidth={1.75} /> Back to all months
            </Link>
          </div>
        </div>
      </section>

      {/* Snapshot cards — render only filled fields */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-center gap-3">
            <span className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] ${inProgress ? "bg-blue-50 border border-blue-100 text-blue-700" : "bg-emerald-50 border border-emerald-100 text-emerald-700"}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${inProgress ? "bg-blue-600 animate-pulse" : "bg-emerald-600"}`} />
              {inProgress ? "Updating daily" : "Complete"}
            </span>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {hasTotal && (
              <div className="bg-[#fafafa] border border-slate-100 rounded-3xl p-7">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-medium">Total</div>
                <div className="mt-3 font-display text-4xl font-light text-slate-900">{data.total_new_companies.toLocaleString("en-IN")}</div>
                <div className="mt-1 text-sm text-slate-500">{data.total_label || "newly registered companies"} · {monthName} {year}</div>
              </div>
            )}
            {hasTopStates && (
              <div className="bg-[#fafafa] border border-slate-100 rounded-3xl p-7">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-medium">Top States</div>
                <ul className="mt-3 space-y-1.5">
                  {data.top_states.slice(0, 5).map((s) => (
                    <li key={s.name || s} className="flex justify-between text-sm">
                      <span className="text-slate-800">{s.name || s}</span>
                      {s.count != null && <span className="text-blue-700 font-medium">{Number(s.count).toLocaleString("en-IN")}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {hasSplit && (
              <div className="bg-[#fafafa] border border-slate-100 rounded-3xl p-7">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-medium">Company Type Split</div>
                <ul className="mt-3 space-y-1.5">
                  {Object.entries(data.company_type_split).map(([k, v]) => (
                    <li key={k} className="flex justify-between text-sm">
                      <span className="text-slate-800">{k}</span>
                      <span className="text-blue-700 font-medium">{typeof v === "number" ? v.toLocaleString("en-IN") : v}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data?.highlight_note && (
              <div className="bg-blue-50/60 border border-blue-100 rounded-3xl p-7 sm:col-span-3">
                <p className="text-slate-700 leading-relaxed">{data.highlight_note}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="py-16 md:py-20 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Contents</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            MCA data for {monthName} {year}: what you get
          </h2>
          <div className="mt-8 space-y-5 text-base md:text-lg text-slate-600 leading-relaxed">
            <p>{angle}</p>
            <p>
              The <strong className="text-slate-900">MCA data {monthName} {year} download</strong> includes company name, CIN,
              registration date, state, ROC, company type, director name, mobile and email, with paid-up capital where
              available. The <strong className="text-slate-900">new company list {monthName} {year} Excel</strong> file ships
              with a matching CSV for CRM import — state, ROC, type and capital-band filters applied at order time.
            </p>
            <p>
              Whether you want to <strong className="text-slate-900">buy MCA company data</strong> for this single month or add it
              to a rolling plan, the <strong className="text-slate-900">newly registered companies {monthName} {year}</strong> file is
              delivered within 24 hours. Teams that <strong className="text-slate-900">buy newly registered companies list</strong>{" "}
              files month after month compound their pipeline — this month's cohort is still choosing vendors right now.
            </p>
          </div>
        </div>
      </section>

      {/* Who buys */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Buyers</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Who buys {monthName} {year} new company data
          </h2>
          <p className="mt-5 text-slate-600 max-w-2xl">
            Companies registered in {monthName} {year} are still choosing vendors in their first 90 days.
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {USE_CASES.map((u) => (
              <div key={u.t} className="bg-[#fafafa] border border-slate-100 rounded-3xl p-6 hover:bg-white hover:shadow-md hover:border-blue-100 transition-all">
                <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <u.icon className="w-[18px] h-[18px] text-blue-600" strokeWidth={1.5} />
                </div>
                <h3 className="mt-5 font-display text-lg font-medium text-slate-900">{u.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  Reach {monthName} {year} incorporations while vendor decisions are still open.
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing mini */}
      <section className="py-16 md:py-20 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Pricing</div>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-light tracking-tight text-slate-900">
            {monthName} {year} data — pricing
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-100 rounded-3xl p-6">
              <div className="font-display text-lg font-medium text-slate-900">Trial</div>
              <div className="mt-1 font-display text-3xl font-light text-slate-900">₹3,000</div>
              <div className="text-sm text-slate-500 mt-1">1-day sample, up to 500 records</div>
            </div>
            <div className="bg-slate-900 text-white rounded-3xl p-6">
              <div className="font-display text-lg font-medium">Monthly Plan</div>
              <div className="mt-1 font-display text-3xl font-light">₹20,000<span className="text-sm text-slate-300">/mo</span></div>
              <div className="text-sm text-slate-300 mt-1">Daily delivery, unlimited records</div>
            </div>
            <a href={wa} target="_blank" rel="noopener noreferrer" className="group bg-white border border-slate-200 rounded-3xl p-6 flex flex-col justify-center hover:border-slate-900 hover:shadow-md transition-all">
              <span className="font-display text-lg font-medium text-slate-900">Buy {monthName} {year} data</span>
              <span className="mt-1 inline-flex items-center gap-1.5 text-sm text-blue-600 font-medium">
                Order on WhatsApp <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Sample table */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-slate-900">
            Sample — {monthName} {year} format
          </h2>
          <div className="mt-8 overflow-x-auto rounded-3xl border border-slate-200 bg-white">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                  {["Company Name", "CIN", "State", "Type", "Incorporation Date"].map((h) => (
                    <th key={h} className="p-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className={`p-4 font-medium text-slate-900 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.company}</td>
                    <td className={`p-4 font-mono text-xs text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.cin}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.state}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.type}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-slate-400">Sample format. Request real sample on WhatsApp.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-slate-900">
            {monthName} {year} — FAQs
          </h2>
          <div className="mt-8 divide-y divide-slate-200">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="py-1">
                  <button onClick={() => setOpenFaq(open ? -1 : i)} aria-expanded={open} className="w-full py-6 flex items-start justify-between gap-6 text-left group">
                    <span className="font-display text-lg font-medium text-slate-900 group-hover:text-blue-600 transition-colors">{f.q}</span>
                    <span className="mt-1 flex-shrink-0 w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center group-hover:border-slate-900 transition-colors">
                      {open ? <Minus className="w-4 h-4" strokeWidth={1.75} /> : <Plus className="w-4 h-4" strokeWidth={1.75} />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35 }} className="overflow-hidden">
                        <p className="pb-6 pr-16 text-slate-600 leading-relaxed">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Prev / Next + other months */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {prev ? (
              <Link to={`/mca-data/${prev}`} className="group inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-900 hover:border-slate-900 transition-colors">
                <ChevronLeft className="w-4 h-4" strokeWidth={1.75} /> {getMonthYear(prev).monthName} {getMonthYear(prev).year}
              </Link>
            ) : <span />}
            <Link to="/mca-data" className="text-sm font-medium text-blue-600 hover:text-blue-700">All months</Link>
            {next ? (
              <Link to={`/mca-data/${next}`} className="group inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-900 hover:border-slate-900 transition-colors">
                {getMonthYear(next).monthName} {getMonthYear(next).year} <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
              </Link>
            ) : <span />}
          </div>
          <div className="mt-10">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-medium mb-4">Other months in {year}</div>
            <div className="flex flex-wrap gap-2">
              {MONTH_SLUGS.filter((s) => s !== slug).map((s) => {
                const { monthName: mn, year: yy } = getMonthYear(s);
                return (
                  <Link key={s} to={`/mca-data/${s}`} className="rounded-full bg-[#fafafa] border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:border-blue-600 hover:text-blue-700 transition-colors">
                    {mn} {yy}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative py-16 md:py-20 bg-slate-900 text-white overflow-hidden">
        <div className="absolute -top-16 -left-16 w-96 h-96 rounded-full bg-blue-600/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">
            Get the <span className="font-serif-editorial italic">{monthName} {year}</span> file in 24 hours
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href={wa} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-7 pr-6 py-5 text-base font-medium hover:brightness-95 active:scale-95 transition-all">
              <MessageCircle className="w-5 h-5" strokeWidth={1.75} /> WhatsApp Now
            </a>
            <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-white text-slate-900 pl-7 pr-6 py-5 text-base font-medium hover:bg-blue-50 hover:text-blue-700 active:scale-95 transition-all">
              Request Access <ArrowUpRight className="w-5 h-5" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>

      <div className="bg-white border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 text-xs text-slate-400">
          MCA registration data is publicly available. Use it for legitimate B2B outreach, honour DND registrations and keep opt-outs current.
        </div>
      </div>
    </div>
  );
}
